import { createClient } from '@supabase/supabase-js';

let supabaseInstance = null;

function getSupabaseConfig() {
  // Try to get environment variables from different sources
  const supabaseUrl = 
    (typeof window !== 'undefined' && window.ENV?.SUPABASE_URL) || 
    (typeof window !== 'undefined' && window.process?.env?.SUPABASE_URL) ||
    (typeof window !== 'undefined' && window.FALLBACK_SUPABASE_URL) ||
    process.env.SUPABASE_URL || 
    process.env.NEXT_PUBLIC_SUPABASE_URL;
    
  const supabaseAnonKey = 
    (typeof window !== 'undefined' && window.ENV?.SUPABASE_ANON_KEY) || 
    (typeof window !== 'undefined' && window.process?.env?.SUPABASE_ANON_KEY) ||
    (typeof window !== 'undefined' && window.FALLBACK_SUPABASE_ANON_KEY) ||
    process.env.SUPABASE_ANON_KEY || 
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    console.error('Missing Supabase environment variables:', {
      url: !!supabaseUrl,
      key: !!supabaseAnonKey
    });
    return null;
  }

  return { supabaseUrl, supabaseAnonKey };
}

export function getSupabaseClient() {
  // If running in browser, try to use the global supabaseClient
  if (typeof window !== 'undefined') {
    // Return existing instance if available
    if (supabaseInstance) {
      return supabaseInstance;
    }
    
    // Try to get the global client
    if (window.supabaseClient) {
      supabaseInstance = window.supabaseClient;
      return supabaseInstance;
    }
    
    // Use the global helper function if available
    if (typeof window.ensureSupabaseClient === 'function') {
      try {
        const client = window.ensureSupabaseClient();
        if (client) {
          supabaseInstance = client;
          return supabaseInstance;
        }
      } catch (error) {
        console.error('Error using global Supabase helper:', error);
      }
    }
  }

  const config = getSupabaseConfig();
  if (!config) {
    console.error('Failed to get Supabase configuration');
    return null;
  }

  try {
    supabaseInstance = createClient(config.supabaseUrl, config.supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true
      }
    });
    return supabaseInstance;
  } catch (error) {
    console.error('Failed to create Supabase client:', error);
    return null;
  }
}

// Initialize the client
export const supabase = getSupabaseClient();

// Helper functions for ESG data
export const esgData = {
  // Get frameworks
  async getFrameworks() {
    const { data, error } = await supabase
      .from('esg_frameworks')
      .select('*');
    
    if (error) throw error;
    return data;
  },

  // Get metrics for a framework
  async getMetrics(frameworkId) {
    const { data, error } = await supabase
      .from('esg_metrics')
      .select('*')
      .eq('framework_id', frameworkId);
    
    if (error) throw error;
    return data;
  },

  // Get company metrics
  async getCompanyMetrics(companyId, frameworkId) {
    const { data, error } = await supabase
      .from('company_metrics')
      .select('*')
      .eq('company_id', companyId)
      .eq('framework_id', frameworkId);
    
    if (error) throw error;
    return data;
  },

  // Update company metrics
  async updateCompanyMetrics(companyId, frameworkId, metrics) {
    const { data, error } = await supabase
      .from('company_metrics')
      .upsert({
        company_id: companyId,
        framework_id: frameworkId,
        metrics: metrics,
        updated_at: new Date().toISOString()
      });
    
    if (error) throw error;
    return data;
  },

  // Generate report
  async generateReport(companyId, frameworkId, options = {}) {
    const { data, error } = await supabase
      .from('esg_reports')
      .insert({
        company_id: companyId,
        framework_id: frameworkId,
        options: options,
        status: 'generating',
        created_at: new Date().toISOString()
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }
}; 